import {
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type QuestionnaireAnswers,
    type IQuestionnaireProps,
    type IQuestionnaireItem,
    type QuestionnaireSize,
    Questionnaire,
    Button
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Questionnaire";

const USAGE_CODE = `<Questionnaire
    items={[
        {
            choices: [
                {
                    title: "Third quarter",
                    value: "q3"
                },
                {
                    title: "Last 30 days",
                    value: "last30"
                }
            ],
            title: "Which period should the report cover?",
            isRequired: true,
            name: "period"
        },
        {
            input: {
                title: "Note"
            },
            title: "Anything to add?",
            selectBehaviour: "none",
            name: "note"
        }
    ]}
    onSubmit={(answers) => sendAnswers(answers)}
    title="Report preferences"
/>`;

const TYPES_CODE = `{
    choices: [
        {
            title: "Tasks",
            value: "tasks"
        },
        {
            description: "Not available for this team",
            title: "Capacity",
            value: "capacity",
            isDisabled: true
        }
    ],
    title: "Which sections should it include?",
    selectBehaviour: "multiple",
    name: "sections"
}`;

const SUBMIT_CODE = `<Questionnaire
    submittedAt={isSubmitted ? submittedAt : undefined}
    isSubmitFailed={isSubmitFailed}
    isSubmitted={isSubmitted}
    onSubmit={handleSubmit}
    isLoading={isLoading}
    title="Report preferences"
    items={items}
/>`;

const SUBMIT_DELAY = 1200;

const ITEM_COUNT = 3;

const createItems = (t: DocsTranslate) => {
    const period: IQuestionnaireItem = {
        input: {
            placeholder: t("questionnairePage.periodOtherPlaceholder"),
            title: t("questionnairePage.periodOther")
        },
        choices: [
            {
                description: t("questionnairePage.periodQ3Range"),
                title: t("questionnairePage.periodQ3"),
                value: "q3"
            },
            {
                description: t("questionnairePage.periodLast30Range"),
                title: t("questionnairePage.periodLast30"),
                value: "last30"
            },
            {
                description: t("questionnairePage.periodYtdRange"),
                title: t("questionnairePage.periodYtd"),
                value: "ytd"
            }
        ],
        description: t("questionnairePage.periodDescription"),
        errorMessage: t("questionnairePage.periodError"),
        label: t("questionnairePage.periodLabel"),
        title: t("questionnairePage.periodTitle"),
        isRequired: true,
        name: "period"
    };

    const sections: IQuestionnaireItem = {
        choices: [
            {
                title: t("questionnairePage.sectionsTasks"),
                value: "tasks"
            },
            {
                title: t("questionnairePage.sectionsBudget"),
                value: "budget"
            },
            {
                description: t("questionnairePage.sectionsCapacityReason"),
                title: t("questionnairePage.sectionsCapacity"),
                value: "capacity",
                isDisabled: true
            },
            {
                title: t("questionnairePage.sectionsRisks"),
                value: "risks"
            }
        ],
        description: t("questionnairePage.sectionsDescription"),
        label: t("questionnairePage.sectionsLabel"),
        title: t("questionnairePage.sectionsTitle"),
        selectBehaviour: "multiple",
        isRequired: true,
        name: "sections"
    };

    const note: IQuestionnaireItem = {
        input: {
            placeholder: t("questionnairePage.notePlaceholder"),
            title: t("questionnairePage.noteLabel"),
            isMultiline: true,
            maxLength: 200
        },
        label: t("questionnairePage.noteLabel"),
        title: t("questionnairePage.noteTitle"),
        selectBehaviour: "none",
        name: "note"
    };

    return {
        sections,
        period,
        note
    };
};

const useSubmitFlow = (isFirstAttemptFailing: boolean) => {
    const [
        answers,
        setAnswers
    ] = useState<QuestionnaireAnswers | undefined>(undefined);

    const [
        attempt,
        setAttempt
    ] = useState(0);

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    const [
        isSubmitFailed,
        setIsSubmitFailed
    ] = useState(false);

    const [
        submittedAt,
        setSubmittedAt
    ] = useState<number | undefined>(undefined);

    useEffect(() => {
        if(!isLoading) {
            return;
        }

        const timer = window.setTimeout(() => {
            setIsLoading(false);

            if(isFirstAttemptFailing && attempt === 1) {
                setIsSubmitFailed(true);

                return;
            }

            setSubmittedAt(Date.now());
        }, SUBMIT_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isFirstAttemptFailing,
        isLoading,
        attempt
    ]);

    const handleSubmit = (nextAnswers: QuestionnaireAnswers) => {
        setAnswers(nextAnswers);

        setAttempt((current) => current + 1);

        setIsSubmitFailed(false);

        setIsLoading(true);
    };

    const handleReset = () => {
        setSubmittedAt(undefined);

        setIsSubmitFailed(false);

        setIsLoading(false);

        setAnswers(undefined);

        setAttempt(0);
    };

    return {
        isSubmitted: submittedAt !== undefined,
        isSubmitFailed,
        handleSubmit,
        handleReset,
        submittedAt,
        isLoading,
        answers
    };
};

// A single question needs no progress bar: the segmented progress always shows at least two steps.
const SingleQuestion = ({
    isDisabled = false,
    onSubmit,
    title,
    item,
    size
}: {
    onSubmit?: IQuestionnaireProps["onSubmit"];
    size?: QuestionnaireSize;
    item: IQuestionnaireItem;
    isDisabled?: boolean;
    title: string;
}) => <Questionnaire
    isReviewEnabled={false}
    isDisabled={isDisabled}
    onSubmit={onSubmit}
    title={title}
    size={size}
    items={[
        item
    ]}
>
    <Questionnaire.Item/>
    {
        isDisabled ?
            <Questionnaire.Status/>
        :
            <Questionnaire.Actions/>
    }
</Questionnaire>;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("questionnairePage.typesTitle"),
        id: "types"
    },
    {
        title: t("questionnairePage.requiredTitle"),
        id: "required"
    },
    {
        title: t("questionnairePage.submitTitle"),
        id: "submit"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const QuestionnairePage = () => {
    const {
        language,
        t
    } = useDocsI18n();

    const items = createItems(t);

    const flow = [
        items.period,
        items.sections,
        items.note
    ];

    const overview = useSubmitFlow(false);

    const review = useSubmitFlow(true);

    const [
        overviewRun,
        setOverviewRun
    ] = useState(0);

    const [
        reviewRun,
        setReviewRun
    ] = useState(0);

    const [
        reviewIndex,
        setReviewIndex
    ] = useState(ITEM_COUNT);

    const [
        typesAnswers,
        setTypesAnswers
    ] = useState<QuestionnaireAnswers | undefined>(undefined);

    const [
        requiredAnswers,
        setRequiredAnswers
    ] = useState<QuestionnaireAnswers | undefined>(undefined);

    const defaultAnswers: QuestionnaireAnswers = {
        note: t("questionnairePage.noteAnswer"),
        period: "q3",
        sections: [
            "tasks",
            "budget"
        ]
    };

    const getAnswersCaption = (answers: QuestionnaireAnswers | undefined, fallback: string) => {
        if(!answers) {
            return fallback;
        }

        return t("questionnairePage.receivedCaption", [
            JSON.stringify(answers)
        ]);
    };

    const getOverviewCaption = () => {
        if(overview.isSubmitted) {
            return getAnswersCaption(overview.answers, t("questionnairePage.overviewCaption"));
        }

        return t("questionnairePage.overviewCaption");
    };

    const getReviewCaption = () => {
        if(review.isSubmitted) {
            return t("questionnairePage.submitDoneCaption");
        }

        if(review.isSubmitFailed) {
            return t("questionnairePage.submitFailedCaption");
        }

        return t("questionnairePage.submitCaption");
    };

    const handleOverviewReset = () => {
        overview.handleReset();

        setOverviewRun((current) => current + 1);
    };

    const handleReviewReset = () => {
        review.handleReset();

        setReviewIndex(ITEM_COUNT);

        setReviewRun((current) => current + 1);
    };

    return <Article>
        <ComponentHeader
            lead={t("questionnairePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("questionnairePage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("questionnairePage.overviewAnswersText")}
            </Paragraph>
            <Preview
                caption={getOverviewCaption()}
                isStacked={true}
            >
                <Questionnaire
                    title={t("questionnairePage.formTitle")}
                    submittedAt={overview.submittedAt}
                    isSubmitted={overview.isSubmitted}
                    onSubmit={overview.handleSubmit}
                    isLoading={overview.isLoading}
                    key={overviewRun}
                    items={flow}
                />
                {
                    overview.isSubmitted ?
                        <Button
                            title={t("questionnairePage.startAgain")}
                            onPress={handleOverviewReset}
                            variant="outline"
                            type="neutral"
                        />
                    :
                        null
                }
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("questionnairePage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("questionnairePage.typesText")}
            </Paragraph>
            <Preview
                caption={getAnswersCaption(typesAnswers, t("questionnairePage.typesCaption"))}
                isStacked={true}
            >
                <SingleQuestion
                    title={t("questionnairePage.formTitle")}
                    onSubmit={setTypesAnswers}
                    item={items.period}
                />
                <SingleQuestion
                    title={t("questionnairePage.formTitle")}
                    onSubmit={setTypesAnswers}
                    item={items.sections}
                />
                <SingleQuestion
                    title={t("questionnairePage.formTitle")}
                    onSubmit={setTypesAnswers}
                    item={items.note}
                />
            </Preview>
            <CodeBlock
                code={TYPES_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("questionnairePage.requiredTitle")}
            id="required"
        >
            <Paragraph>
                {t("questionnairePage.requiredText")}
            </Paragraph>
            <Preview
                caption={getAnswersCaption(requiredAnswers, t("questionnairePage.requiredCaption"))}
                isStacked={true}
            >
                <Questionnaire
                    title={t("questionnairePage.formTitle")}
                    onSubmit={setRequiredAnswers}
                    isReviewEnabled={false}
                    items={[
                        items.period,
                        items.note
                    ]}
                />
            </Preview>
        </Section>
        <Section
            title={t("questionnairePage.submitTitle")}
            id="submit"
        >
            <Paragraph>
                {t("questionnairePage.submitText")}
            </Paragraph>
            <Preview
                caption={getReviewCaption()}
                isStacked={true}
            >
                <Questionnaire
                    submitErrorText={t("questionnairePage.submitErrorText")}
                    title={t("questionnairePage.formTitle")}
                    isSubmitFailed={review.isSubmitFailed}
                    onActiveIndexChange={setReviewIndex}
                    submittedAt={review.submittedAt}
                    isSubmitted={review.isSubmitted}
                    key={`${language}-${reviewRun}`}
                    defaultAnswers={defaultAnswers}
                    onSubmit={review.handleSubmit}
                    isLoading={review.isLoading}
                    activeIndex={reviewIndex}
                    items={flow}
                />
                {
                    review.isSubmitted ?
                        <Button
                            title={t("questionnairePage.startAgain")}
                            onPress={handleReviewReset}
                            variant="outline"
                            type="neutral"
                        />
                    :
                        null
                }
            </Preview>
            <CodeBlock
                code={SUBMIT_CODE}
                language="tsx"
            />
            <Preview
                caption={t("questionnairePage.disabledCaption")}
                isStacked={true}
            >
                <SingleQuestion
                    title={t("questionnairePage.formTitle")}
                    item={items.period}
                    isDisabled={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("questionnairePage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <SingleQuestion
                    title={t("questionnairePage.formTitle")}
                    item={items.sections}
                    size="medium"
                />
                <SingleQuestion
                    title={t("questionnairePage.formTitle")}
                    item={items.sections}
                    size="large"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("questionnairePage.a11yForm"),
                    t("questionnairePage.a11yProgress"),
                    t("questionnairePage.a11yQuestion"),
                    t("questionnairePage.a11yChoices"),
                    t("questionnairePage.a11yKeyboard"),
                    t("questionnairePage.a11yReview"),
                    t("questionnairePage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("questionnairePage.propItems"),
                        type: "Array<IQuestionnaireItem>",
                        defaultValue: "[]",
                        name: "items"
                    },
                    {
                        description: t("questionnairePage.propTitle"),
                        name: "title",
                        type: "string"
                    },
                    {
                        description: t("questionnairePage.propOnSubmit"),
                        type: "(answers: QuestionnaireAnswers) => void",
                        name: "onSubmit"
                    },
                    {
                        description: t("questionnairePage.propDefaultAnswers"),
                        type: "QuestionnaireAnswers",
                        name: "defaultAnswers",
                        defaultValue: "{}"
                    },
                    {
                        description: t("questionnairePage.propActiveIndex"),
                        name: "activeIndex",
                        type: "number"
                    },
                    {
                        description: t("questionnairePage.propOnActiveIndexChange"),
                        type: "(index: number) => void",
                        name: "onActiveIndexChange"
                    },
                    {
                        description: t("questionnairePage.propIsReviewEnabled"),
                        name: "isReviewEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("questionnairePage.propSize"),
                        type: "\"medium\" | \"large\"",
                        name: "size"
                    },
                    {
                        description: t("questionnairePage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("questionnairePage.propIsSubmitFailed"),
                        name: "isSubmitFailed",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("questionnairePage.propSubmitErrorText"),
                        name: "submitErrorText",
                        type: "string"
                    },
                    {
                        description: t("questionnairePage.propIsSubmitted"),
                        defaultValue: "false",
                        name: "isSubmitted",
                        type: "boolean"
                    },
                    {
                        description: t("questionnairePage.propSubmittedAt"),
                        type: "Date | number",
                        name: "submittedAt"
                    },
                    {
                        description: t("questionnairePage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("questionnairePage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("questionnairePage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("questionnairePage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph>
                {t("questionnairePage.itemIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("questionnairePage.itemName"),
                        type: "string",
                        name: "name"
                    },
                    {
                        description: t("questionnairePage.itemTitle"),
                        name: "title",
                        type: "string"
                    },
                    {
                        description: t("questionnairePage.itemLabel"),
                        name: "label",
                        type: "string"
                    },
                    {
                        description: t("questionnairePage.itemDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        description: t("questionnairePage.itemChoices"),
                        type: "Array<IQuestionnaireChoice>",
                        name: "choices"
                    },
                    {
                        description: t("questionnairePage.itemInput"),
                        type: "IQuestionnaireInput",
                        name: "input"
                    },
                    {
                        description: t("questionnairePage.itemSelectBehaviour"),
                        type: "\"single\" | \"multiple\" | \"none\"",
                        defaultValue: "\"single\"",
                        name: "selectBehaviour"
                    },
                    {
                        description: t("questionnairePage.itemIsRequired"),
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("questionnairePage.itemErrorMessage"),
                        name: "errorMessage",
                        type: "string"
                    }
                ]}
            />
            <Paragraph>
                {t("questionnairePage.choiceIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("questionnairePage.choiceValue"),
                        name: "value",
                        type: "string"
                    },
                    {
                        description: t("questionnairePage.choiceTitle"),
                        name: "title",
                        type: "string"
                    },
                    {
                        description: t("questionnairePage.choiceDescription"),
                        name: "description",
                        type: "string"
                    },
                    {
                        description: t("questionnairePage.choiceShortcut"),
                        name: "shortcut",
                        type: "string"
                    },
                    {
                        description: t("questionnairePage.choiceIsDisabled"),
                        name: "isDisabled",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                {t("questionnairePage.inputIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("questionnairePage.inputTitle"),
                        name: "title",
                        type: "string"
                    },
                    {
                        description: t("questionnairePage.inputPlaceholder"),
                        name: "placeholder",
                        type: "string"
                    },
                    {
                        description: t("questionnairePage.inputHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        description: t("questionnairePage.inputMaxLength"),
                        name: "maxLength",
                        type: "number"
                    },
                    {
                        description: t("questionnairePage.inputIsMultiline"),
                        name: "isMultiline",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                {t("questionnairePage.partsIntro")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        type: "{ item?: IQuestionnaireItem; children?: ReactNode }",
                        description: t("questionnairePage.partItem"),
                        name: "Questionnaire.Item"
                    },
                    {
                        description: t("questionnairePage.partTitle"),
                        type: "{ children?: ReactNode }",
                        name: "Questionnaire.Title"
                    },
                    {
                        description: t("questionnairePage.partDescription"),
                        name: "Questionnaire.Description",
                        type: "{ children?: ReactNode }"
                    },
                    {
                        description: t("questionnairePage.partChoices"),
                        type: "{ children?: ReactNode }",
                        name: "Questionnaire.Choices"
                    },
                    {
                        type: "{ choice: IQuestionnaireChoice; index: number }",
                        description: t("questionnairePage.partChoice"),
                        name: "Questionnaire.Choice"
                    },
                    {
                        description: t("questionnairePage.partInput"),
                        name: "Questionnaire.Input",
                        type: "{}"
                    },
                    {
                        description: t("questionnairePage.partError"),
                        type: "{ children?: ReactNode }",
                        name: "Questionnaire.Error"
                    },
                    {
                        description: t("questionnairePage.partProgress"),
                        name: "Questionnaire.Progress",
                        type: "{}"
                    },
                    {
                        type: "{ isDivided?: boolean; children?: ReactNode }",
                        description: t("questionnairePage.partActions"),
                        name: "Questionnaire.Actions"
                    },
                    {
                        description: t("questionnairePage.partReview"),
                        name: "Questionnaire.Review",
                        type: "{}"
                    },
                    {
                        description: t("questionnairePage.partStatus"),
                        name: "Questionnaire.Status",
                        type: "{}"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("questionnairePage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default QuestionnairePage;
